import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, useEffect } from 'react';
import { pricesApi, stationsApi } from '@/services/api';
import { RoutingService } from '@/services/routing';
import { GeolocationService } from '@/services/geolocation';
import { performVisualClusteringWithPricing, getVisualClusteringDebugInfo } from '@/utils/visualClustering';
import type { 
  CurrentPrice, 
  StationRecommendation, 
  Location, 
  VehicleConfig, 
  SearchFilters,
  FuelStation
} from '@/types';
import type { VisualClusteringResult, VisualClusterData } from '@/utils/visualClustering';

/**
 * Default vehicle configurations
 */
export const DEFAULT_VEHICLE_CONFIGS: Record<string, VehicleConfig> = {
  petrol: {
    fuelType: 'petrol',
    costPerMile: 15, // pence per mile
  },
  diesel: {
    fuelType: 'diesel',
    costPerMile: 12, // pence per mile
  },
  hybrid: {
    fuelType: 'petrol',
    costPerMile: 8, // pence per mile
  },
  electric: {
    fuelType: 'electric',
    costPerMile: 4, // pence per mile
  },
};

/**
 * Hook to get current fuel prices with optional filtering
 */
export function useCurrentPrices(filters?: {
  fuel_code?: string;
  postcode?: string;
  retailer?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['prices', 'current', filters],
    queryFn: () => pricesApi.getCurrent(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get nearby stations based on user location
 */
export function useNearbyStations(
  userLocation: Location | null,
  filters: SearchFilters
) {
  return useQuery({
    queryKey: ['stations', 'nearby', userLocation?.latitude, userLocation?.longitude, filters.radius, filters.brands],
    queryFn: () => {
      if (!userLocation) return [];
      
      return stationsApi.getNearby({
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        radius: filters.radius,
        active_only: true,
        limit: 100,
      });
    },
    enabled: !!userLocation,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get prices for specific station IDs
 */
export function usePricesForStations(
  stationIds: number[],
  fuelCode?: string
) {
  return useQuery({
    queryKey: ['prices', 'by-stations', stationIds, fuelCode],
    queryFn: () => {
      if (stationIds.length === 0) return [];
      return pricesApi.getByStations(stationIds, {
        fuel_code: fuelCode,
        sort_by_price: true,
      });
    },
    enabled: stationIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get station recommendations based on user location and preferences
 */
export function useStationRecommendations(
  userLocation: Location | null,
  vehicleConfig: VehicleConfig,
  filters: SearchFilters,
  fuelAmountLiters: number = 50
) {
  // First, get nearby stations
  const { data: nearbyStations, isLoading: stationsLoading, error: stationsError } = useNearbyStations(
    userLocation,
    filters
  );

  // Extract station IDs from nearby stations
  const stationIds = nearbyStations?.map(station => station.id) || [];

  // Then get prices for only those specific nearby stations
  const { data: pricesData, isLoading: pricesLoading, error: pricesError } = usePricesForStations(
    stationIds,
    filters.fuelTypes[0] // Use first fuel type for now
  );

  // State for routing-based recommendations
  const [routingRecommendations, setRoutingRecommendations] = useState<StationRecommendation[]>([]);
  const [isCalculatingRoutes, setIsCalculatingRoutes] = useState(false);

  // Calculate routing-based recommendations when data changes
  useEffect(() => {
    if (!userLocation || !nearbyStations || !pricesData || nearbyStations.length === 0) {
      setRoutingRecommendations([]);
      return;
    }

    const processStationsWithRouting = async () => {
      setIsCalculatingRoutes(true);
      
      try {
        // Group prices by station ID (prices are already filtered to nearby stations)
        const stationPricesMap = new Map<number, CurrentPrice[]>();
        pricesData.forEach(price => {
          if (!stationPricesMap.has(price.station_id)) {
            stationPricesMap.set(price.station_id, []);
          }
          stationPricesMap.get(price.station_id)!.push(price);
        });

        const recommendations: StationRecommendation[] = [];
        
        // Collect valid stations for batch processing
        const validStations: { station: FuelStation; bestPrice: CurrentPrice; relevantPrices: CurrentPrice[] }[] = [];
        
        // Pre-filter stations and prices
        for (const station of nearbyStations) {
          // Apply brand filter
          if (filters.brands.length > 0 && !filters.brands.includes(station.brand)) {
            continue;
          }

          const prices = stationPricesMap.get(station.id) || [];
          
          // Find best price for relevant fuel types
          const relevantPrices = prices.filter(price => 
            filters.fuelTypes.length === 0 || filters.fuelTypes.includes(price.fuel_code)
          );

          if (relevantPrices.length === 0) continue;

          // Find the cheapest fuel price at this station
          const bestPrice = relevantPrices.reduce((best, current) => {
            if (!best.price_pence) return current;
            if (!current.price_pence) return best;
            return current.price_pence < best.price_pence ? current : best;
          });

          if (!bestPrice.price_pence) continue;

          // Apply max price filter
          if (filters.maxPrice && bestPrice.price_pence > filters.maxPrice) {
            continue;
          }

          validStations.push({ station, bestPrice, relevantPrices });
        }

        // Calculate routes for all valid stations using batch processing
        try {
          const destinations = validStations.map(({ station }) => station.location);
          const routeInfos = await RoutingService.calculateMultipleRoutes(userLocation, destinations, 'driving-car');

          // Process each station with its corresponding route info
          validStations.forEach(({ station, bestPrice, relevantPrices }, index) => {
            const routeInfo = routeInfos[index];
            const distance = routeInfo.distance;
            const travelTime = routeInfo.duration;

            // Calculate travel cost based on trip type and actual routing distance
            const tripMultiplier = filters.tripType === 'return' ? 2 : 1;
            const travelCost = Math.round((distance * tripMultiplier * vehicleConfig.costPerMile));
            
            // Calculate fuel cost (amount in liters * price per liter)
            const fuelCost = Math.round(fuelAmountLiters * (bestPrice.price_pence || 0));
            
            // Calculate total cost (fuel + travel)
            const totalCost = fuelCost + travelCost;

            recommendations.push({
              station,
              prices: relevantPrices,
              bestPrice,
              distance,
              routeInfo,
              travelTime,
              travelCost,
              fuelCost,
              totalCost,
            });
          });

        } catch (error) {
          console.warn('Routing calculation failed, falling back to straight-line distance:', error);
          
          // Fallback to straight-line distance calculation
          validStations.forEach(({ station, bestPrice, relevantPrices }) => {
            // Use distance from backend if available, otherwise calculate
            const distance = station.distance_miles || GeolocationService.calculateDistance(
              userLocation,
              station.location
            );

            // Calculate travel cost based on trip type
            const tripMultiplier = filters.tripType === 'return' ? 2 : 1;
            const travelCost = Math.round((distance * tripMultiplier * vehicleConfig.costPerMile));
            
            // Calculate fuel cost (amount in liters * price per liter)
            const fuelCost = Math.round(fuelAmountLiters * (bestPrice.price_pence || 0));
            
            // Calculate total cost (fuel + travel)
            const totalCost = fuelCost + travelCost;

            recommendations.push({
              station,
              prices: relevantPrices,
              bestPrice,
              distance,
              travelCost,
              fuelCost,
              totalCost,
            });
          });
        }

        setRoutingRecommendations(recommendations);
      } catch (error) {
        console.error('Error processing stations with routing:', error);
        setRoutingRecommendations([]);
      } finally {
        setIsCalculatingRoutes(false);
      }
    };

    processStationsWithRouting();
  }, [nearbyStations, pricesData, userLocation, vehicleConfig, filters, fuelAmountLiters]);

  // Process final recommendations with sorting and savings calculation
  const recommendations = useMemo((): { 
    recommendations: StationRecommendation[], 
    averageTotalCost: number, 
    averageFuelPrice: number, 
    minFuelPrice: number, 
    maxFuelPrice: number, 
    priceGap: number,
    minPriceStation: StationRecommendation | null,
    maxPriceStation: StationRecommendation | null
  } => {
    if (routingRecommendations.length === 0) return { 
      recommendations: [], 
      averageTotalCost: 0, 
      averageFuelPrice: 0, 
      minFuelPrice: 0, 
      maxFuelPrice: 0, 
      priceGap: 0,
      minPriceStation: null,
      maxPriceStation: null
    };

    const sortedRecommendations = [...routingRecommendations];

    // Sort recommendations
    sortedRecommendations.sort((a, b) => {
      switch (filters.sortBy) {
        case 'price':
          return (a.bestPrice?.price_pence || 0) - (b.bestPrice?.price_pence || 0);
        case 'distance':
          return a.distance - b.distance;
        case 'total_cost':
          return a.totalCost - b.totalCost;
        case 'savings':
          return (b.savings || 0) - (a.savings || 0);
        default:
          return a.totalCost - b.totalCost;
      }
    });

    // Calculate price statistics for the area
    let averageTotalCost = 0;
    let averageFuelPrice = 0;
    let minFuelPrice = 0;
    let maxFuelPrice = 0;
    let priceGap = 0;
    let minPriceStation: StationRecommendation | null = null;
    let maxPriceStation: StationRecommendation | null = null;
    
    if (sortedRecommendations.length > 0) {
      const pricesWithStations = sortedRecommendations
        .map(rec => ({ price: rec.bestPrice?.price_pence || 0, station: rec }))
        .filter(item => item.price > 0);
      
      averageTotalCost = sortedRecommendations.reduce((sum, rec) => sum + rec.totalCost, 0) / sortedRecommendations.length;
      
      if (pricesWithStations.length > 0) {
        const prices = pricesWithStations.map(item => item.price);
        averageFuelPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
        minFuelPrice = Math.min(...prices);
        maxFuelPrice = Math.max(...prices);
        priceGap = maxFuelPrice - minFuelPrice;
        
        // Find stations with min and max prices
        minPriceStation = pricesWithStations.find(item => item.price === minFuelPrice)?.station || null;
        maxPriceStation = pricesWithStations.find(item => item.price === maxFuelPrice)?.station || null;
      }
      
      sortedRecommendations.forEach(rec => {
        // Calculate savings based on fuel price difference (not total cost)
        const fuelPriceDifference = averageFuelPrice - (rec.bestPrice?.price_pence || 0);
        rec.savings = Math.round(fuelPriceDifference * fuelAmountLiters); // Savings on fuel cost for this amount
        if (rec.distance > 0) {
          rec.efficiency = (rec.savings || 0) / (rec.distance * 2); // Savings per mile of travel
        }
      });
    }

    return { 
      recommendations: sortedRecommendations, 
      averageTotalCost, 
      averageFuelPrice,
      minFuelPrice,
      maxFuelPrice,
      priceGap,
      minPriceStation,
      maxPriceStation
    };
  }, [routingRecommendations, filters, fuelAmountLiters]);

  return {
    data: recommendations.recommendations,
    averageTotalCost: recommendations.averageTotalCost,
    averageFuelPrice: recommendations.averageFuelPrice,
    minFuelPrice: recommendations.minFuelPrice,
    maxFuelPrice: recommendations.maxFuelPrice,
    priceGap: recommendations.priceGap,
    minPriceStation: recommendations.minPriceStation,
    maxPriceStation: recommendations.maxPriceStation,
    isLoading: stationsLoading || pricesLoading || isCalculatingRoutes,
    error: stationsError || pricesError,
  };
}

/**
 * Hook to get cheapest stations for a specific fuel type
 */
export function useCheapestStations(
  fuelCode: string,
  postcode?: string,
  limit = 10
) {
  return useQuery({
    queryKey: ['prices', 'cheapest', fuelCode, postcode, limit],
    queryFn: () => pricesApi.getCheapest({ fuel_code: fuelCode, postcode, limit }),
    enabled: !!fuelCode,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to calculate route information for recommendations
 */
export function useRouteCalculation(
  userLocation: Location | null,
  recommendations: StationRecommendation[]
) {
  return useQuery({
    queryKey: ['routes', userLocation?.latitude, userLocation?.longitude, recommendations.map(r => r.station.id)],
    queryFn: async () => {
      if (!userLocation || recommendations.length === 0) return [];

      const destinations = recommendations.map(r => r.station.location);
      return await RoutingService.calculateMultipleRoutes(userLocation, destinations);
    },
    enabled: !!userLocation && recommendations.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get price summary for a postcode area
 */
export function usePriceSummary(postcodeArea: string, fuelCode?: string) {
  return useQuery({
    queryKey: ['prices', 'summary', postcodeArea, fuelCode],
    queryFn: () => pricesApi.getByPostcode(postcodeArea, fuelCode),
    enabled: !!postcodeArea,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get clustered stations for map visualization
 */
export function useStationClusters(
  zoomLevel: number,
  mapBounds?: {
    south: number;
    north: number;
    west: number;
    east: number;
  },
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['stations', 'clustered', zoomLevel, mapBounds],
    queryFn: () => {
      return stationsApi.getClustered({
        zoom_level: zoomLevel,
        south: mapBounds?.south,
        north: mapBounds?.north,
        west: mapBounds?.west,
        east: mapBounds?.east,
        active_only: true,
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - more frequent updates for viewport changes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get individual stations for high zoom levels
 */
export function useViewportStations(
  mapBounds?: {
    south: number;
    north: number;
    west: number;
    east: number;
  },
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['stations', 'viewport', mapBounds],
    queryFn: async () => {
      if (!mapBounds) return [];
      
      // Calculate center and radius from bounds for nearby stations API
      const centerLat = (mapBounds.south + mapBounds.north) / 2;
      const centerLng = (mapBounds.west + mapBounds.east) / 2;
      
      // Calculate accurate radius using proper diagonal distance
      // More precise degree-to-miles conversion
      const MILES_PER_DEGREE_LAT = 69.0; // Constant for latitude
      const MILES_PER_DEGREE_LNG = 69.0 * Math.cos(centerLat * Math.PI / 180); // Adjusted for longitude
      
      // Half-dimensions of the viewport
      const latHalfSpan = (mapBounds.north - mapBounds.south) / 2 * MILES_PER_DEGREE_LAT;
      const lngHalfSpan = (mapBounds.east - mapBounds.west) / 2 * MILES_PER_DEGREE_LNG;
      
      // Use diagonal distance for complete viewport coverage
      const diagonalRadius = Math.sqrt(latHalfSpan * latHalfSpan + lngHalfSpan * lngHalfSpan);
      const radius = diagonalRadius;
      
      // Debug logging to show the improvement
      const oldLatDistance = (mapBounds.north - mapBounds.south) * 69;
      const oldLngDistance = (mapBounds.east - mapBounds.west) * Math.cos(centerLat * Math.PI / 180) * 69;
      const oldRadius = Math.max(oldLatDistance, oldLngDistance) / 2 * 1.2;
      
      console.log('📏 Viewport radius calculation:', {
        viewportDimensions: {
          latSpan: (mapBounds.north - mapBounds.south).toFixed(4) + '°',
          lngSpan: (mapBounds.east - mapBounds.west).toFixed(4) + '°',
        },
        distances: {
          latHalfSpan: latHalfSpan.toFixed(1) + ' miles',
          lngHalfSpan: lngHalfSpan.toFixed(1) + ' miles',
          diagonal: diagonalRadius.toFixed(1) + ' miles',
        },
        radiusComparison: {
          oldMethod: oldRadius.toFixed(1) + ' miles',
          newMethod: radius.toFixed(1) + ' miles',
          improvement: ((oldRadius - radius) / oldRadius * 100).toFixed(1) + '% smaller'
        }
      });
      
      // Reasonable radius cap to prevent excessive API calls
      const maxRadius = Math.min(radius, 200); // Reduced cap from 500 to 200 miles
      
      return stationsApi.getNearby({
        latitude: centerLat,
        longitude: centerLng,
        radius: maxRadius,
        active_only: true,
        limit: 1000, // Increase limit for better coverage
      });
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes - stations change more frequently than clusters
    refetchOnWindowFocus: false,
  });
}

/**
 * Consolidated hook to get viewport stations with price data and recommendations
 * Replaces both useViewportStationRecommendations and useViewportStationsWithPrices
 */
export function useViewportStationsWithPrices(
  mapBounds?: {
    south: number;
    north: number;
    west: number;
    east: number;
  },
  fuelCode: string = 'B7',
  enabled: boolean = true,
  userLocation?: Location | null
): {
  data: StationRecommendation[];
  isLoading: boolean;
  error: any;
} {
  // Get viewport stations
  const { data: viewportStations, isLoading: stationsLoading, error: stationsError } = useViewportStations(
    mapBounds,
    enabled
  );

  // Get station IDs for price fetching
  const stationIds = useMemo(() => 
    viewportStations?.map(station => station.id) || [], 
    [viewportStations]
  );

  // Fetch prices for viewport stations
  const { data: pricesData, isLoading: pricesLoading, error: pricesError } = usePricesForStations(
    stationIds,
    fuelCode
  );

  // Combine station and price data into recommendations
  const recommendations = useMemo((): StationRecommendation[] => {
    if (!viewportStations || !pricesData || viewportStations.length === 0) return [];

    // Group prices by station ID for efficient lookup
    const stationPricesMap = new Map<number, CurrentPrice[]>();
    pricesData.forEach(price => {
      if (!stationPricesMap.has(price.station_id)) {
        stationPricesMap.set(price.station_id, []);
      }
      stationPricesMap.get(price.station_id)!.push(price);
    });

    const recommendations: StationRecommendation[] = [];

    // Process each viewport station
    for (const station of viewportStations) {
      const prices = stationPricesMap.get(station.id) || [];
      
      // Find best price for the selected fuel type
      const relevantPrices = prices.filter(price => price.fuel_code === fuelCode);
      const bestPrice = relevantPrices.length > 0 
        ? relevantPrices.reduce((best, current) => {
            if (!best.price_pence) return current;
            if (!current.price_pence) return best;
            return current.price_pence < best.price_pence ? current : best;
          })
        : undefined;

      const distance = userLocation
        ? GeolocationService.calculateDistance(userLocation, station.location)
        : station.distance_miles || 0;

      recommendations.push({
        station,
        prices: relevantPrices,
        bestPrice,
        distance,
        travelCost: 0, // No travel cost calculation for viewport exploration
        fuelCost: bestPrice?.price_pence || 0,
        totalCost: bestPrice?.price_pence || 0,
      });
    }

    return recommendations;
  }, [viewportStations, pricesData, fuelCode, userLocation]);

  return {
    data: recommendations,
    isLoading: stationsLoading || pricesLoading,
    error: stationsError || pricesError,
  };
}

/**
 * @deprecated Use useViewportStationsWithPrices instead
 * Legacy alias maintained for backwards compatibility
 */
export function useViewportStationRecommendations(
  mapBounds?: {
    south: number;
    north: number;
    west: number;
    east: number;
  },
  fuelCode: string = 'B7',
  enabled: boolean = true
): {
  data: StationRecommendation[];
  isLoading: boolean;
  error: any;
} {
  return useViewportStationsWithPrices(mapBounds, fuelCode, enabled);
}

/**
 * Hook for visual density-based clustering
 * Clusters stations based on pixel overlap rather than geographic distance
 */
export function useVisualClustering(
  stationRecommendations: StationRecommendation[],
  zoomLevel: number,
  mapCenter: Location | null,
  mapSize: { width: number; height: number },
  options: {
    minClusterSize?: number;
    padding?: number;
    maxStations?: number;
    enabled?: boolean;
    fuelCode?: string; // Add fuel code for price filtering
    mapBounds?: { south: number; north: number; west: number; east: number }; // Add map bounds for DBSCAN
    clusteringThreshold?: number; // Zoom level above which clustering is skipped
  } = {}
): {
  clusters: VisualClusterData[];
  individualStations: StationRecommendation[];
  isProcessing: boolean;
  debugInfo: string;
  priceRange?: { min: number; max: number }; // Add price range for color scaling
} {
  const {
    minClusterSize = 2,
    padding = 8,
    maxStations = 500,
    enabled = true,
    fuelCode = 'B7', // Default to B7 diesel
    mapBounds,
    clusteringThreshold = 11,
  } = options;

  const [clusteringResult, setClusteringResult] = useState<VisualClusteringResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!enabled || !mapCenter || stationRecommendations.length === 0) {
      setClusteringResult(null);
      setIsProcessing(false);
      return;
    }

    // Synchronous early-exit: above the clustering threshold, immediately show all
    // stations as individuals and clear any stale cluster result from a previous
    // low-zoom run. This prevents old clusters from flashing when exploration mode
    // re-engages at a high zoom level.
    if (zoomLevel > clusteringThreshold) {
      setClusteringResult({
        clusters: [],
        individualStations: stationRecommendations.map(r => r.station),
        debugInfo: {
          totalStations: stationRecommendations.length,
          clusteredStations: 0,
          individualStations: stationRecommendations.length,
          processingTime: 0,
        },
      });
      setIsProcessing(false);
      return;
    }

    let cancelled = false;

    const performClustering = async () => {
      if (cancelled) return;
      
      setIsProcessing(true);
      
      try {
        console.log('🔍 Visual Clustering Input:', {
          stationCount: stationRecommendations.length,
          zoomLevel,
          mapCenter,
          mapSize,
          fuelCode,
          options
        });

        // Use the enhanced DBSCAN clustering algorithm with pricing data
        const result = await performVisualClusteringWithPricing(
          stationRecommendations,
          fuelCode,
          zoomLevel,
          mapCenter,
          mapSize,
          {
            minClusterSize,
            padding,
            maxStations,
            mapBounds
          }
        );

        if (!cancelled) {
          console.log('📊 Enhanced Visual Clustering Result:', getVisualClusteringDebugInfo(result));
          setClusteringResult(result);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('❌ Clustering failed:', error);
          setClusteringResult(null);
        }
      } finally {
        if (!cancelled) {
          setIsProcessing(false);
        }
      }
    };

    performClustering();
    
    return () => {
      cancelled = true;
    };
  }, [
    stationRecommendations,
    zoomLevel, 
    mapCenter?.latitude,
    mapCenter?.longitude,
    mapSize.width,
    mapSize.height,
    minClusterSize,
    padding,
    maxStations,
    enabled,
    fuelCode,
    clusteringThreshold,
    mapBounds?.south,
    mapBounds?.north,
    mapBounds?.west,
    mapBounds?.east
  ]);

  // Convert individual stations back to recommendations with their original price data
  const individualRecommendations = useMemo((): StationRecommendation[] => {
    if (!clusteringResult) return [];
    
    const stationRecommendationMap = new Map<number, StationRecommendation>();
    stationRecommendations.forEach(rec => {
      stationRecommendationMap.set(rec.station.id, rec);
    });
    
    return clusteringResult.individualStations.map(station => {
      return stationRecommendationMap.get(station.id) || {
        station,
        prices: [],
        bestPrice: undefined,
        distance: station.distance_miles || 0,
        travelCost: 0,
        fuelCost: 0,
        totalCost: 0,
      };
    });
  }, [clusteringResult, stationRecommendations]);

  // Calculate global price range for color scaling
  const priceRange = useMemo(() => {
    if (!clusteringResult) return undefined;
    
    const clustersWithPrices = clusteringResult.clusters.filter(c => c.average_price_pence);
    if (clustersWithPrices.length === 0) return undefined;
    
    const prices = clustersWithPrices.map(c => c.average_price_pence!);
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  }, [clusteringResult]);

  return {
    clusters: clusteringResult?.clusters || [],
    individualStations: individualRecommendations,
    isProcessing, // Now reflects actual async processing state
    debugInfo: clusteringResult ? getVisualClusteringDebugInfo(clusteringResult) : 'No clustering data available',
    priceRange,
  };
}